/**
 * Company Search — entity header (sealed element a).
 *
 * Renders the company name (threaded from the lookup selection), a monogram
 * derived from that name, and — when the lookup provides one — a subdued match
 * line from match_basis (lookup_company v2). Named bases map to a prose label;
 * any other token renders raw in the mono register (no invented label); an
 * absent basis renders nothing. Watch / Export / Prep actions are out of scope.
 */

import { matchBasisLabel } from "../logic";
import { IconBuilding } from "./icons";

/** Up to two leading initials from the display name; null when unavailable. */
function monogramOf(name: string | null): string | null {
  if (!name) return null;
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const letters = words.slice(0, 2).map((w) => w.charAt(0)).join("");
  return letters ? letters.toUpperCase() : null;
}

export function EntityHeader({
  displayName,
  matchBasis,
}: {
  displayName: string | null;
  matchBasis: string | null;
}): JSX.Element {
  const monogram = monogramOf(displayName);
  const match = matchBasisLabel(matchBasis);
  return (
    <header className="sxco-entity">
      <span className="sxco-monogram" aria-hidden="true">
        {monogram ?? <IconBuilding />}
      </span>
      <div className="sxco-entity-text">
        <h2 className="sxco-entity-name">{displayName ?? "—"}</h2>
        {match && (
          <p className={`sxco-entity-match${match.mono ? " sxco-entity-match--mono" : ""}`}>
            {match.text}
          </p>
        )}
      </div>
    </header>
  );
}
